import { useEffect, useRef } from 'react';

import { DeeplinkKeys } from '@/consts/deepLinks';
import { getPresetIdFromUrl } from '@/utils/project/getPresetIdFromUrl';
import { getLibraryItemInfo } from '@/requests/library/getLibraryItemInfo';
import { setAppStore, getAppStore } from '@/store';
import { extractUrlParams } from '@/utils/ui/extractUrlParams';

import type { IInput } from '@/types';

/**
 * Hook to handle preset deeplinks (presetId URL parameter).
 * Similar to useCategoryDeeplink but for loading a specific preset.
 *
 * When a presetId is in the URL:
 * 1. Sets hasAutoSelectedPreset to prevent useLibrary auto-select
 * 2. Fetches the preset from the API
 * 3. Sets the preset in the store and navigates to create screen
 */
const usePresetDeeplink = () => {
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Prevent double loading in React Strict Mode
    if (hasLoadedRef.current) return;

    // Skip if task_id is present - notification handler will handle navigation
    const { [DeeplinkKeys.taskId]: taskId } = extractUrlParams([
      DeeplinkKeys.taskId,
    ]);
    if (taskId) return;

    const { presetId } = getPresetIdFromUrl();
    if (!presetId) return;

    hasLoadedRef.current = true;
    let isCancelled = false;

    // Immediately set flags to prevent useLibrary auto-select race condition
    // and to skip intro video for deeplink sessions
    setAppStore({ hasAutoSelectedPreset: true, isRemoteProject: true });

    const loadPreset = async () => {
      try {
        const preset = await getLibraryItemInfo(presetId);

        // Check if effect was cleaned up while loading
        if (isCancelled) return;

        if (!preset) {
          console.log('[usePresetDeeplink] Preset not found:', presetId);
          // Reset flag so normal auto-select can work
          setAppStore({ hasAutoSelectedPreset: false });
          return;
        }

        // Safely handle missing textItems or uploadItems
        const textItems = preset.textItems ?? [];
        const uploadItems = preset.uploadItems ?? [];
        const inputs: IInput[] = [...textItems, ...uploadItems];

        // Check if preset is already set (e.g., by useProjectInit)
        const { selectedPreset } = getAppStore();
        if (selectedPreset?.flowId === preset.flowId) {
          console.log('[usePresetDeeplink] Preset already loaded');
          return;
        }

        // Set preset and navigate to create screen
        // Also set activeTabSlug to preset's category and trigger scroll
        setAppStore({
          selectedPreset: preset,
          inputs,
          screen: 'create',
          // Set active tab to preset's category for proper gallery context
          ...(preset.category && { activeTabSlug: preset.category }),
          // Trigger scroll to active tab in HorizontalTabs
          shouldScrollToActiveTab: true,
        });

        console.log('[usePresetDeeplink] Loaded preset:', preset);
      } catch (error) {
        // Check if effect was cleaned up while loading
        if (isCancelled) return;

        console.error('[usePresetDeeplink] Failed to load preset:', error);
        // Reset flag so normal auto-select can work
        setAppStore({ hasAutoSelectedPreset: false });
      }
    };

    loadPreset();

    return () => {
      isCancelled = true;
    };
  }, []);
};

export { usePresetDeeplink };
